import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Upload } from "lucide-react";
import { API, api } from "../../lib/api";

export default function MediaLibrary() {
  const [items, setItems] = useState(null);
  const fileRef = useRef(null);

  const load = () => api("/admin/media").then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(`${API}/admin/media`, { method: "POST", body: form, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      toast("Uploaded — copy its URL into any product or image slot");
      load();
    } catch (err) { toast.error(err.message); }
    e.target.value = "";
  };

  const remove = async (id) => {
    try { await api(`/admin/media/${id}`, { method: "DELETE" }); toast("Deleted"); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-semibold text-2xl">Media library</h1>
          <p className="text-muted text-sm font-semibold">Upload images here, then paste the URL into a product image or any image slot.</p>
        </div>
        <button data-testid="admin-media-upload" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-candy text-white font-bold text-sm hover:bg-candy-deep transition-colors duration-200">
          <Upload className="w-4 h-4" /> Upload
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml,video/mp4" onChange={upload} className="hidden" aria-hidden="true" />
      </div>
      {!items ? <p className="text-muted font-bold mt-8">Opening the craft drawer…</p> : items.length === 0 ? (
        <p className="text-muted font-semibold mt-8">Nothing uploaded yet.</p>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} className="bg-white rounded-3xl border border-candy-soft/70 p-3" data-testid={`admin-media-${m.id}`}>
              <img src={`${API}/media/${m.id}`} alt="" className="w-full h-28 object-cover rounded-2xl bg-candy-mist" />
              <p className="text-xs font-bold truncate mt-2">{m.filename}</p>
              <div className="flex gap-1 mt-1">
                <button data-testid={`admin-media-copy-${m.id}`} onClick={() => { navigator.clipboard.writeText(`${API}/media/${m.id}`); toast("URL copied"); }} className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 rounded-lg bg-candy-mist text-candy-deep text-xs font-bold hover:bg-candy-soft transition-colors duration-200">
                  <Copy className="w-3 h-3" /> Copy URL
                </button>
                <button data-testid={`admin-media-delete-${m.id}`} onClick={() => remove(m.id)} className="px-2 py-1.5 rounded-lg bg-candy-mist text-red-500 text-xs font-bold hover:bg-red-50 transition-colors duration-200">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
